/**
 * Sheet Metal Utilities — flat pattern computation, bend table generation.
 * Uses K-factor method for bend allowance calculations.
 */

import type { Feature, SheetMetalBaseFeature, SheetMetalFlangeFeature } from "./featureTypes";

export interface BendEntry {
  flangeId: string;
  flangeName: string;
  edge: string;
  angle: number;
  bendRadius: number;
  thickness: number;
  kFactor: number;
  bendAllowance: number;
  developedLength: number;
}

export interface FlatPatternData {
  /** Total flat width (X) in mm */
  totalWidth: number;
  /** Total flat length (Y) in mm */
  totalLength: number;
  /** Thickness of the sheet */
  thickness: number;
  /** List of bend entries */
  bends: BendEntry[];
  /** Replicad script to generate the flat pattern shape */
  flatScript: string;
}

/**
 * Calculate bend allowance using K-factor method.
 * BA = angle(rad) × (bendRadius + kFactor × thickness)
 */
export function bendAllowance(angleDegs: number, bendRadius: number, thickness: number, kFactor: number): number {
  const angleRad = (angleDegs * Math.PI) / 180;
  return angleRad * (bendRadius + kFactor * thickness);
}

/**
 * Compute the flat pattern from a sheet metal feature tree.
 * Returns the flat dimensions, bend table, and a script that generates the flat shape.
 */
export function computeFlatPattern(features: Feature[]): FlatPatternData | null {
  const base = features.find((f) => f.type === "sheetMetalBase" && f.enabled) as SheetMetalBaseFeature | undefined;
  if (!base) return null;

  const flanges = features.filter(
    (f) => f.type === "sheetMetalFlange" && f.enabled && (f as SheetMetalFlangeFeature).targetRef === base.id
  ) as SheetMetalFlangeFeature[];

  const bends: BendEntry[] = [];
  let extraWidth = 0;
  let extraLength = 0;

  for (const fl of flanges) {
    const ba = bendAllowance(fl.flangeAngle, base.bendRadius, base.thickness, base.kFactor);
    const developed = fl.flangeLength + ba;

    bends.push({
      flangeId: fl.id,
      flangeName: fl.name,
      edge: fl.edge,
      angle: fl.flangeAngle,
      bendRadius: base.bendRadius,
      thickness: base.thickness,
      kFactor: base.kFactor,
      bendAllowance: Math.round(ba * 100) / 100,
      developedLength: Math.round(developed * 100) / 100,
    });

    if (fl.edge === "left" || fl.edge === "right") extraWidth += developed;
    else extraLength += developed;
  }

  const totalWidth = base.width + extraWidth;
  const totalLength = base.length + extraLength;

  // Generate flat pattern script — a single flat box with the developed dimensions
  const flatScript = `const { makeBox } = replicad;

function main() {
  return makeBox(${totalWidth.toFixed(2)}, ${totalLength.toFixed(2)}, ${base.thickness});
}`;

  return {
    totalWidth: Math.round(totalWidth * 100) / 100,
    totalLength: Math.round(totalLength * 100) / 100,
    thickness: base.thickness,
    bends,
    flatScript,
  };
}

/**
 * Generate DXF string for a flat pattern (rectangular outline).
 * Standard DXF R12 format — compatible with laser cutters and CNC.
 */
export function flatPatternToDxf(pattern: FlatPatternData): string {
  const w = pattern.totalWidth;
  const h = pattern.totalLength;

  // Compute bend line positions
  const bendLines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  let leftOffset = 0;
  let bottomOffset = 0;

  for (const bend of pattern.bends) {
    if (bend.edge === "left") {
      leftOffset += bend.developedLength;
      bendLines.push({ x1: leftOffset, y1: 0, x2: leftOffset, y2: h });
    } else if (bend.edge === "right") {
      bendLines.push({ x1: w - leftOffset - bend.developedLength, y1: 0, x2: w - leftOffset - bend.developedLength, y2: h });
    } else if (bend.edge === "bottom") {
      bottomOffset += bend.developedLength;
      bendLines.push({ x1: 0, y1: bottomOffset, x2: w, y2: bottomOffset });
    } else if (bend.edge === "top") {
      bendLines.push({ x1: 0, y1: h - bottomOffset - bend.developedLength, x2: w, y2: h - bottomOffset - bend.developedLength });
    }
  }

  const lines: string[] = [
    "0", "SECTION", "2", "ENTITIES",
    // Outer rectangle
    "0", "LINE", "8", "OUTLINE", "10", "0", "20", "0", "11", `${w}`, "21", "0",
    "0", "LINE", "8", "OUTLINE", "10", `${w}`, "20", "0", "11", `${w}`, "21", `${h}`,
    "0", "LINE", "8", "OUTLINE", "10", `${w}`, "20", `${h}`, "11", "0", "21", `${h}`,
    "0", "LINE", "8", "OUTLINE", "10", "0", "20", `${h}`, "11", "0", "21", "0",
  ];

  // Bend lines (on BEND layer, dashed)
  for (const bl of bendLines) {
    lines.push("0", "LINE", "8", "BEND", "6", "DASHED",
      "10", `${bl.x1}`, "20", `${bl.y1}`,
      "11", `${bl.x2}`, "21", `${bl.y2}`);
  }

  lines.push("0", "ENDSEC", "0", "EOF");
  return lines.join("\n");
}

/**
 * Apply corner relief to a sheet metal base.
 * Returns the relief cut dimensions based on thickness and bend radius.
 */
export function cornerReliefSize(thickness: number, bendRadius: number): number {
  // Standard corner relief: diameter = thickness + bendRadius
  return thickness + bendRadius;
}
